import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismButtonGroup } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import SettingsRecommendationsSnowflakesRecommendationsSettingCard from 'lumen/components/settings/recommendations/snowflakes/recommendations-setting-card';
import type { RecommendationStrategy } from 'lumen/models';
import type { RoundingSettings } from 'lumen/models/recommendation-strategy';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import { safeRollback } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsRecommendationsRoundingSignature {
  Args: {
    model: RecommendationsModel;
    strategy: RecommendationStrategy | null;
  };
}

export default class SettingsRecommendationsRounding extends Component<SettingsRecommendationsRoundingSignature> {
  @service declare access: Services['access'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isEditMode = false;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRoundingNumbers() {
    const roundingNumbers = [];

    let number = 0;

    if (this.strategy && this.strategy.settings.settings_type === 'rounding') {
      while (number < 10) {
        const settings = this.strategy.settings;
        roundingNumbers.push({
          id: number.toString(),
          value: number.toString(),
          isSelected: settings.rounding_values.includes(number),
        });
        number += 1;
      }
    }

    return roundingNumbers;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRoundingSettingEnabled() {
    if (this.strategy) {
      return (this.strategy.settings as RoundingSettings).enabled;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    if (this.strategy) {
      return !this.strategy.hasDirtyAttributes;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRoundingNumbers() {
    return this.formattedRoundingNumbers
      .filter((button) => button.isSelected)
      .map((button) => button.id);
  }

  // Edge case: If user makes a change and then navigates away to another page (seasonal strategy subroute) without saving/canceling and then comes back
  // Rollback the unsaved changes on destroy
  willDestroy(): void {
    super.willDestroy();

    safeRollback(this.strategy);
  }

  @action onCancelClick(): void {
    // Reset the rounding strategy
    if (this.strategy) {
      this.strategy.rollbackAttributes();
    }

    this.isEditMode = false;
  }

  @action onEditClick(): void {
    this.isEditMode = true;
  }

  @action onRoundingNumberButtonClick(
    selectedButtons: string | string[],
  ): void {
    if (
      !this.strategy?.settings ||
      this.strategy.settings.settings_type !== 'rounding'
    ) {
      return;
    }

    const selectedValues = Array.isArray(selectedButtons)
      ? selectedButtons
      : [selectedButtons];

    this.strategy.settings = {
      ...this.strategy.settings,
      rounding_values: selectedValues.map(Number),
    };
  }

  @action
  async onSaveClick(): Promise<void> {
    if (this.strategy) {
      await this.args.model.saveStrategyTask.perform(this.strategy);
    }

    this.isEditMode = false;
  }

  @action
  async onToggle(): Promise<void> {
    if (this.strategy && this.strategy.settings.settings_type === 'rounding') {
      // rollback changes
      this.strategy.rollbackAttributes();

      // update the enabled status
      const settings = this.strategy.settings;
      this.strategy.settings = {
        ...settings,
        enabled: !settings.enabled,
      };

      await this.args.model.saveStrategyTask.perform(this.strategy);

      if (this.strategy.settings.enabled) {
        this.usageAnalytics.track('Price rounding enabled', {
          eventTrigger: 'recommendation-settings',
          product: 'pricing-manager',
        });
      }
    }

    // Reset edit mode
    this.isEditMode = false;
  }

  <template>
    <SettingsRecommendationsSnowflakesRecommendationsSettingCard
      @description={{t
        "pricing_assistant.recommendations.settings.price_rounding.description"
      }}
      @handleToggleChange={{this.onToggle}}
      @hasToggle={{true}}
      @illustration="40x40_rounding"
      @isToggleDisabled={{this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled}}
      @isToggled={{this.isRoundingSettingEnabled}}
      @title={{t
        "pricing_assistant.recommendations.settings.price_rounding.title"
      }}
    >
      <:content>
        <div class="flex flex-row">
          <PrismButtonGroup
            @isDisabled={{not this.isEditMode}}
            @onChange={{this.onRoundingNumberButtonClick}}
            @value={{this.selectedRoundingNumbers}}
            as |ButtonGroup|
          >
            {{#each this.formattedRoundingNumbers as |formattedRoundingNumber|}}
              <ButtonGroup.button
                @label={{formattedRoundingNumber.value}}
                @value={{formattedRoundingNumber.id}}
                class="recommendations-setting-card__buttongroup-button"
              />
            {{/each}}
          </PrismButtonGroup>
          {{#unless
            this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
          }}
            <div class="ml-400">
              {{#if this.isEditMode}}
                <div class="flex flex-row gap-200">
                  <PrismButton
                    @buttonType="secondary"
                    @label={{t "general.cancel"}}
                    @onClick={{this.onCancelClick}}
                  />
                  <PrismButton
                    @isDisabled={{this.isSaveButtonDisabled}}
                    @label={{t "general.save"}}
                    @onClick={{this.onSaveClick}}
                  />
                </div>
              {{else}}
                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="edit-pencil"
                  @label={{t "general.edit"}}
                  @onClick={{this.onEditClick}}
                />
              {{/if}}
            </div>
          {{/unless}}
        </div>
      </:content>
    </SettingsRecommendationsSnowflakesRecommendationsSettingCard>
  </template>
}
